import { useEffect, useMemo, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { IconAlertTriangle, IconLoader2 } from "@tabler/icons-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { CalOrganizationUser, CalTeam } from "@/app/booking-calendar/bookingCalendarApi"
import { AffectedBookingsList } from "@/app/booking-calendar/components/AffectedBookingsList"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { useAppSelector } from "@/store"
import {
  type CreateScheduleOverrideResult,
  type EventTypeSlug,
  type OverrideKind,
  type ScheduleOverridePreview,
  useCreateScheduleOverrideMutation,
  usePreviewScheduleOverrideQuery,
} from "../scheduleOverridesApi"
import {
  buildRequest,
  clientErrors,
  DAY_END,
  DAY_START,
  emptyForm,
  EVENT_TYPE_CHOICES,
  type EventTypeScope,
  fieldsFor,
  formatDateRange,
  KIND,
  KIND_DESCRIPTIONS,
  KIND_LABELS,
  type OverrideFormState,
  reassignmentWording,
} from "../lib/overrideForm"
import { releaseNotes } from "../lib/releaseNotice"

// shadcn's Select cannot hold an empty value, so an optional site uses this for "all of them".
const ALL = "__all__"

const KINDS: OverrideKind[] = [2, 1, 0, 3]

const SCOPES: { value: EventTypeScope; label: string }[] = [
  { value: "site", label: "Whole site" },
  { value: "hostAtSite", label: "One host at one site" },
  { value: "host", label: "One host, every site" },
]

interface NewOverrideDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  teams: CalTeam[]
  hosts: CalOrganizationUser[]
  /** Today in the sites' zone, "yyyy-MM-dd". */
  today: string
}

export function NewOverrideDialog({ open, onOpenChange, teams, hosts, today }: NewOverrideDialogProps) {
  const [form, setForm] = useState<OverrideFormState>(() => emptyForm(today))
  const [submitErrors, setSubmitErrors] = useState<string[]>([])
  const [createOverride, { isLoading: saving }] = useCreateScheduleOverrideMutation()

  useEffect(() => {
    if (open) {
      setForm(emptyForm(today))
      setSubmitErrors([])
    }
  }, [open, today])

  // Read as the route reads it: absent (no slice, not yet read) means off, which is how the server
  // plans a reassignment then too (AB#805).
  const weekViewOn = useAppSelector((state) => state.features?.features.amsAvailabilityWeekView ?? false)
  const reassignment = reassignmentWording(weekViewOn)
  const fields = fieldsFor(form, weekViewOn)
  const localErrors = clientErrors(form, today)
  const request = useMemo(() => buildRequest(form), [form])
  const requestKey = localErrors.length === 0 ? JSON.stringify(request) : null

  // The form as it stood once it stopped changing for a moment: what the preview is asked about.
  const settledKey = useDebouncedValue(requestKey, 400)

  useEffect(() => {
    setSubmitErrors([])
  }, [requestKey])

  // A query keyed by the request, so a late answer for a form since changed is never shown — it is
  // another cache entry — and releasing or moving one of its bookings asks for it again (AB#769).
  const settled = open && requestKey !== null && settledKey === requestKey
  const previewQuery = usePreviewScheduleOverrideQuery(settled && request ? request : skipToken)
  const preview = previewFor(settled, previewQuery)
  const previewing = !preview || previewQuery.isFetching

  const update = (patch: Partial<OverrideFormState>) => setForm((current) => ({ ...current, ...patch }))

  const sites = teams.filter((t): t is CalTeam & { slug: string } => !!t.slug)
  const sortedHosts = [...hosts].sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email))

  const canSubmit =
    !!request && localErrors.length === 0 && !!preview && preview.errors.length === 0 && !previewing && !saving

  async function handleSubmit() {
    if (!request) return

    try {
      const result = await createOverride(request).unwrap()
      const created = result.override?.override

      if (created?.status === 5) {
        toast.error("Override saved, but none of its days could be applied, so it is not in force.", {
          description: "Open it from the list to see why, then withdraw it and try again.",
        })
      } else if (created && (created.failedDays > 0 || created.status !== 1)) {
        toast.warning("Override saved, but some days could not be applied.", {
          description: `${created.failedDays} day(s) failed. Open it from the list to see which.`,
        })
      } else {
        toast.success("Override saved and applied.")
      }

      onOpenChange(false)
    } catch (err) {
      const error = err as { status?: number; data?: unknown }

      if (error.status === 400 && isCreateResult(error.data)) {
        setSubmitErrors(error.data.errors)
      } else if (error.status === 409) {
        setSubmitErrors([typeof error.data === "string" ? error.data : "Another override change is in progress. Try again in a moment."])
      } else if (error.status === 403) {
        setSubmitErrors(["You do not have permission to create schedule overrides."])
      } else {
        setSubmitErrors(["Could not save the override. Try again in a moment."])
      }
    }
  }

  const errors = submitErrors.length > 0 ? submitErrors : (preview?.errors ?? [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New schedule override</DialogTitle>
          <DialogDescription>
            Changes when a site or a host can be booked. It takes effect straight away, and withdrawing it later
            undoes exactly what it changed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <RadioGroup
            value={form.kind === null ? "" : String(form.kind)}
            onValueChange={(value) => update({ kind: Number(value) as OverrideKind })}
            className="grid gap-2 sm:grid-cols-2"
          >
            {KINDS.map((kind) => (
              <Label
                key={kind}
                htmlFor={`override-kind-${kind}`}
                className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer has-[[data-state=checked]]:border-primary"
              >
                <RadioGroupItem id={`override-kind-${kind}`} value={String(kind)} className="mt-0.5" />
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{kind === KIND.outreachAssignment ? reassignment.kindLabel : KIND_LABELS[kind]}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {kind === KIND.outreachAssignment ? reassignment.kindDescription : KIND_DESCRIPTIONS[kind]}
                  </span>
                </div>
              </Label>
            ))}
          </RadioGroup>

          {form.kind !== null && (
            <>
              {fields.scope && (
                <div className="flex flex-col gap-2">
                  <Label>Applies to</Label>
                  <RadioGroup
                    value={form.scope}
                    onValueChange={(value) => update({ scope: value as EventTypeScope })}
                    className="flex flex-wrap gap-4"
                  >
                    {SCOPES.map((scope) => (
                      <Label key={scope.value} htmlFor={`override-scope-${scope.value}`} className="flex items-center gap-2 font-normal">
                        <RadioGroupItem id={`override-scope-${scope.value}`} value={scope.value} />
                        {scope.label}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                {fields.host !== "hidden" && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="override-host">Host</Label>
                    <Select value={form.hostEmail} onValueChange={(value) => update({ hostEmail: value })}>
                      <SelectTrigger id="override-host" className="w-full">
                        <SelectValue placeholder="Choose a host" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedHosts.map((host) => (
                          <SelectItem key={host.id} value={host.email}>
                            {host.name ? `${host.name} (${host.email})` : host.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {fields.outreachSite !== "hidden" && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="override-outreach-site">{reassignment.targetLabel}</Label>
                    <Select value={form.outreachTeamSlug} onValueChange={(value) => update({ outreachTeamSlug: value })}>
                      <SelectTrigger id="override-outreach-site" className="w-full">
                        <SelectValue placeholder="Where they will be" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.slug}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {fields.site !== "hidden" && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="override-site">{fields.siteLabel}</Label>
                    <Select
                      value={form.teamSlug || (fields.site === "optional" ? ALL : "")}
                      onValueChange={(value) => update({ teamSlug: value === ALL ? "" : value })}
                    >
                      <SelectTrigger id="override-site" className="w-full">
                        <SelectValue placeholder="Choose a site" />
                      </SelectTrigger>
                      <SelectContent>
                        {fields.site === "optional" && <SelectItem value={ALL}>{fields.siteAllLabel}</SelectItem>}
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.slug}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {fields.eventType !== "hidden" && (
                  <div className="flex flex-col gap-2">
                    <Label>Run</Label>
                    <RadioGroup
                      value={form.eventTypeSlug}
                      onValueChange={(value) => update({ eventTypeSlug: value as EventTypeSlug })}
                      className="flex flex-col gap-2"
                    >
                      {EVENT_TYPE_CHOICES.map((choice) => (
                        <Label
                          key={choice.value}
                          htmlFor={`override-et-${choice.value}`}
                          className="flex items-center gap-2 font-normal"
                        >
                          <RadioGroupItem id={`override-et-${choice.value}`} value={choice.value} />
                          {choice.label}
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="override-start">Starts</Label>
                  <Input
                    id="override-start"
                    type="datetime-local"
                    min={`${today}T${DAY_START}`}
                    value={form.start}
                    onChange={(e) => {
                      const start = e.target.value
                      // Carry the end along to the end of the new first day rather than leave a
                      // backwards range to be corrected.
                      const carried = `${start.slice(0, 10)}T${DAY_END}`
                      update({ start, end: start && form.end <= start && carried > start ? carried : form.end })
                    }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="override-end">Ends</Label>
                  <Input
                    id="override-end"
                    type="datetime-local"
                    min={form.start || `${today}T${DAY_START}`}
                    value={form.end}
                    onChange={(e) => update({ end: e.target.value })}
                  />
                </div>
                <p className="sm:col-span-2 -mt-2 text-xs text-muted-foreground">
                  12:00 AM to 11:59 PM covers whole days. 11:59 PM runs to the end of the day.
                </p>
              </div>

              {fields.note !== "hidden" && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="override-note">
                    Note{fields.note === "optional" && <span className="text-muted-foreground font-normal"> (optional)</span>}
                  </Label>
                  <Textarea
                    id="override-note"
                    value={form.note}
                    maxLength={200}
                    placeholder="Shown on the staff calendar over the affected days — e.g. “Out sick”, “Snow closure”."
                    onChange={(e) => update({ note: e.target.value })}
                  />
                </div>
              )}

              <PreviewPanel
                localErrors={localErrors}
                errors={errors}
                preview={preview}
                previewing={!preview}
                kind={form.kind}
              />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {saving && <IconLoader2 className="size-4 animate-spin" />}
            Save and apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PreviewPanel({
  localErrors,
  errors,
  preview,
  previewing,
  kind,
}: {
  localErrors: string[]
  errors: string[]
  preview: ScheduleOverridePreview | null
  previewing: boolean
  kind: OverrideKind
}) {
  if (localErrors.length > 0) {
    return <p className="text-sm text-muted-foreground">{localErrors[0]}</p>
  }

  if (previewing || !preview) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <IconLoader2 className="size-4 animate-spin" />
        Checking…
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-3 text-sm">
      {errors.length > 0 ? (
        <div className="flex flex-col gap-1 text-destructive">
          <span className="font-medium">This can't be made:</span>
          <ul className="list-disc pl-5">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="font-medium">What this will change</span>
          <ul className="flex flex-col gap-1">
            {preview.lines.map((line) => (
              <li key={`${line.teamName}|${line.target}|${line.windows}`}>
                <span className="font-medium">{line.teamName}</span> · {line.target}: {line.windows} ·{" "}
                <span className="text-muted-foreground">
                  {line.days} {line.days === 1 ? "day" : "days"} ({formatDateRange(line.firstDate, line.lastDate)})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview.warnings.length > 0 && (
        <ul className="flex flex-col gap-1">
          {preview.warnings.map((warning) => (
            <li key={warning} className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
              <IconAlertTriangle className="size-4 shrink-0 mt-0.5" />
              {warning}
            </li>
          ))}
        </ul>
      )}

      {errors.length === 0 && !affectsBookings(kind) && (
        <p className="text-muted-foreground">
          Appointments already booked in this window are not cancelled — check the calendar for any that need moving.
        </p>
      )}

      {errors.length === 0 && affectsBookings(kind) && (
        <div className="flex flex-col gap-2">
          <span className="font-medium">Appointments already booked</span>
          <p className="text-muted-foreground">
            Saving does not cancel these. Move, release or cancel each one here, now or from the override once it is
            saved.
          </p>
          <AffectedBookingsList
            bookings={preview.bookings ?? []}
            timeZone={SITE_TIME_ZONE}
            canManage
            offerReassign={kind !== SITE_CLOSURE}
            releaseNotes={(booking) => releaseNotes(booking)}
            emptyMessage="No booked appointments fall in this window."
          />
        </div>
      )}
    </div>
  )
}

/** AmsScheduleOverrideKind.SiteClosure: nobody inside it can take a moved appointment. */
export const SITE_CLOSURE: OverrideKind = 2

/**
 * Out of office, a site closure and a temporary site reassignment (kind 0, which closes the host at
 * their other sites, AB#805) make somebody unavailable; an event-type override lists no bookings.
 */
export function affectsBookings(kind: OverrideKind | null): boolean {
  return kind === 0 || kind === 1 || kind === SITE_CLOSURE
}

/** What the panel shows: the latest answer for the settled form, a failure as an error, or nothing yet. */
export function previewFor(
  settled: boolean,
  query: { currentData?: ScheduleOverridePreview; isError: boolean },
): ScheduleOverridePreview | null {
  if (!settled) return null
  if (query.isError) return { errors: ["Could not check this override. Try again in a moment."], warnings: [], lines: [] }
  return query.currentData ?? null
}

/** `value`, once it has stayed the same for `delayMs`. Shared with the availability week's override form (AB#806). */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return settled
}

function isCreateResult(data: unknown): data is CreateScheduleOverrideResult {
  return typeof data === "object" && data !== null && Array.isArray((data as CreateScheduleOverrideResult).errors)
}
